import { useTranslation } from 'next-i18next';
import { useState, VFC } from 'react';

import { useReleaseReminder } from '@/apis/aniftyBackend/releaseReminders/getReleaseReminder';
import { useUpdateReleaseReminder } from '@/apis/aniftyBackend/releaseReminders/updateReleaseReminder';
import CustomButton from '@/components/CustomButton';
import FloatingModal from '@/components/FloatingModal';
import ReleaseReminderCompletedModal from '@/components/ReleaseReminderSetButton/ReleaseReminderCompletedModal';
import useAccessToken from '@/hooks/useAccessToken';
import useWalletAddress from '@/hooks/useWalletAddress';

import styles from './ReleaseReminderSetButton.module.scss';

type Props = {
  creationID: number;
};

const ReleaseReminderSetButton: VFC<Props> = ({ creationID }: Props) => {
  const { t } = useTranslation();
  const { currentAccountAddress } = useWalletAddress();
  const {
    accessToken,
    requestSignature,
    loading: accessTokenLoading,
  } = useAccessToken();

  const { data: isReminderSet, status } = useReleaseReminder({
    creationID,
    accessTokenExists: !!accessToken,
  });
  const [isCompletedModalOpen, setIsCompletedModalOpen] = useState(false);

  const {
    mutateAsync: updateReleaseReminder,
    status: updateReleaseReminderStatus,
    reset,
  } = useUpdateReleaseReminder({ creationID });

  const isLoading =
    status === 'loading' || updateReleaseReminderStatus === 'loading';

  const handleReleaseReminderToggleButtonClick = async () => {
    if (isLoading) {
      return;
    }

    const shouldSetReleaseReminder = !isReminderSet;

    await updateReleaseReminder({
      shouldSetReleaseReminder,
    });

    if (shouldSetReleaseReminder) {
      setIsCompletedModalOpen(true);
    }
  };

  if (!currentAccountAddress) {
    // Wallet is not connected
    return null;
  }

  if (!accessToken) {
    return (
      <CustomButton
        variant="primary"
        onClick={requestSignature}
        loading={accessTokenLoading}
        className={styles.releaseReminderSetButton}
      >
        {t('releaseReminder.buttons.checkStatus')}
      </CustomButton>
    );
  }

  return (
    <div>
      <CustomButton
        variant={isReminderSet ? 'primaryOutlined' : 'primary'}
        onClick={handleReleaseReminderToggleButtonClick}
        loading={isLoading}
        className={styles.releaseReminderSetButton}
      >
        {isReminderSet
          ? t('releaseReminder.buttons.cancelNotification')
          : t('releaseReminder.buttons.getNotified')}
      </CustomButton>

      {/* Completed modal */}
      <ReleaseReminderCompletedModal
        creationID={creationID}
        isOpen={isCompletedModalOpen}
        onRequestClose={() => setIsCompletedModalOpen(false)}
      />

      {/* Error notification modal */}
      <FloatingModal
        isOpen={updateReleaseReminderStatus === 'error'}
        onRequestClose={reset}
      >
        {t('serverError')}
      </FloatingModal>
    </div>
  );
};

export default ReleaseReminderSetButton;
